import React, { useRef, useState } from "react";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import Autocomplete from "@material-ui/lab/Autocomplete";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import { useTranslation } from "react-i18next";

type AutocompletePrediction = google.maps.places.AutocompletePrediction;
type AutocompleteService = google.maps.places.AutocompleteService;
type AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken;
type AutocompletionRequest = google.maps.places.AutocompletionRequest;
type PlacesServiceStatus = google.maps.places.PlacesServiceStatus;

function loadScript(src: string, position: HTMLElement | null, id: string) {
	if (!position) {
		return;
	}

	const script = document.createElement("script");
	script.setAttribute("async", "");
	script.setAttribute("id", id);
	script.src = src;
	position.appendChild(script);
}

const autocompleteService: {
	current: null | AutocompleteService;
} = { current: null };

const useStyles = makeStyles(theme => ({
	icon: {
		color: theme.palette.text.secondary,
		marginRight: theme.spacing(2)
	}
}));

type PlaceType = AutocompletePrediction;

// interface PlaceType {
// 	description: string;
// 	structured_formatting: {
// 		main_text: string;
// 		secondary_text: string;
// 		main_text_matched_substrings: [
// 			{
// 				offset: number;
// 				length: number;
// 			}
// 		];
// 	};
// }

interface PlacesAutocompleteProps {
	className?: string;
	placeholder?: string;
	defaultValue?: null | PlaceType;
	origin?: null | {
		latitude: number;
		longitude: number;
	};
	radius?: number;

	onChange?(event: React.ChangeEvent<{}>, value: null | PlaceType);
}

const PlacesAutocomplete = ({
	className,
	placeholder,
	defaultValue,
	origin,
	radius,
	onChange
}: PlacesAutocompleteProps) => {
	const sessionToken = useRef<AutocompleteSessionToken>();
	const classes = useStyles();
	const [value, setValue] = useState<PlaceType | null>(defaultValue || null);
	const [inputValue, setInputValue] = useState("");
	const [options, setOptions] = useState<PlaceType[]>([]);
	const loaded = useRef(false);
	const { t } = useTranslation();

	if (typeof window !== "undefined" && !loaded.current) {
		if (!document.querySelector("#google-maps")) {
			loadScript(
				`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_API_KEY}&libraries=places,geometry`,
				document.querySelector("head"),
				"google-maps"
			);
		}

		loaded.current = true;
	}

	const fetch = React.useMemo<
		(
			request: AutocompletionRequest,
			callback: (result: AutocompletePrediction[], status: PlacesServiceStatus) => void
		) => void
	>(
		() =>
			throttle(
				(
					request: AutocompletionRequest,
					callback: (result: AutocompletePrediction[], status: PlacesServiceStatus) => void
				) => {
					autocompleteService.current!.getPlacePredictions(request, callback);
				},
				200
			),
		[]
	);

	React.useEffect(() => {
		let active = true;

		if (!autocompleteService.current && (window as any).google) {
			autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
		}
		if (!autocompleteService.current) {
			return undefined;
		}

		if (!sessionToken.current) {
			sessionToken.current = new google.maps.places.AutocompleteSessionToken();
		}

		if (inputValue === "") {
			setOptions(value ? [value] : []);
			return undefined;
		}

		let bounds: undefined | google.maps.LatLngBounds;

		if (origin && radius) {
			const center = new google.maps.LatLng(origin.latitude, origin.longitude);
			const distanceFromCenterToCorner = radius * Math.sqrt(2);
			const swCorner = google.maps.geometry.spherical.computeOffset(center, distanceFromCenterToCorner, 225);
			const neCorner = google.maps.geometry.spherical.computeOffset(center, distanceFromCenterToCorner, 45);
			bounds = new google.maps.LatLngBounds(swCorner, neCorner);
		}

		fetch(
			{
				input: inputValue,
				origin: origin
					? {
							lat: origin.latitude,
							lng: origin.longitude
					  }
					: undefined,
				bounds,
				componentRestrictions: {
					country: "US"
				},
				sessionToken: sessionToken.current
			},
			results => {
				if (active) {
					let newOptions = [] as PlaceType[];

					if (value) {
						newOptions = [value];
					}

					if (results) {
						if (radius) {
							// eslint-disable-next-line no-param-reassign
							results = results.filter(x => x.distance_meters! <= radius);
						}

						newOptions = [...newOptions, ...results];
					}

					setOptions(newOptions);
				}
			}
		);

		return () => {
			active = false;
		};
	}, [origin, radius, value, inputValue, fetch]);

	return (
		<Autocomplete
			fullWidth
			getOptionLabel={option => (typeof option === "string" ? option : option.description)}
			filterOptions={x => x}
			options={options}
			noOptionsText={inputValue ? t("outOfRangeForDelivery") : ""}
			autoComplete
			includeInputInList
			filterSelectedOptions
			value={value}
			onChange={(event: any, newValue: PlaceType | null) => {
				setOptions(newValue ? [newValue, ...options] : options);
				setValue(newValue);

				if (onChange) {
					onChange(event, newValue);
				}

				sessionToken.current = undefined;
			}}
			onInputChange={(event, newInputValue) => {
				setInputValue(newInputValue);
			}}
			renderInput={params => (
				<TextField
					{...params}
					className={className}
					placeholder={placeholder}
					variant="outlined"
					fullWidth
					InputProps={{
						...params.InputProps,
						type: "search",
						style: { padding: 0, borderWidth: 0 },
						autoComplete: "new-password"
					}}
					inputProps={{
						...params.inputProps,
						style: { padding: 0, borderWidth: 0, paddingLeft: 12 }
					}}
				/>
			)}
			renderOption={option => {
				const matches = option.structured_formatting.main_text_matched_substrings;
				const parts = parse(
					option.structured_formatting.main_text,
					matches.map((match: any) => [match.offset, match.offset + match.length])
				);

				return (
					<Grid container alignItems="center">
						<Grid item>
							<LocationOnIcon className={classes.icon} />
						</Grid>
						<Grid item xs>
							{parts.map((part, index) => (
								<span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
									{part.text}
								</span>
							))}
							<Typography variant="body2" color="textSecondary">
								{option.structured_formatting.secondary_text}
							</Typography>
						</Grid>
					</Grid>
				);
			}}
		/>
	);
};

export default PlacesAutocomplete;
